import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/context/LanguageContext";
import { GOALS, goalKey, primaryGoal, memberGoals } from "@/lib/goals";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const ALLERGENS = ["gluten", "dairy", "nuts", "eggs", "fish", "shellfish", "soy"];
const HEALTH_CONTEXTS = ["breastfeeding", "pcos", "endometriosis", "adenomyosis", "insulin_resistance", "diabetes"];
const OPTS = {
  sex: ["female", "male", "other"],
  activity_level: ["sedentary", "light", "moderate", "active", "very_active"],
  goals: GOALS,
  diet: ["omnivore", "vegetarian", "vegan", "pescatarian", "mediterranean", "anti_inflammatory"],
};
const EMPTY = { name: "", age: "", sex: "female", height_cm: "", weight_kg: "", activity_level: "moderate", goals: ["maintain"],
  diet: "omnivore", allergies: [], health_contexts: [], excluded_foods: [], liked_foods: [], disliked_foods: [], participates: true, present_days: DAYS, water_goal: 8 };

function Chips({ options, value, onChange, label, testid, multi = true }) {
  const toggle = (o) => multi ? onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]) : onChange(o);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const on = multi ? value.includes(o) : value === o;
        return (
          <button type="button" key={o} data-testid={`${testid}-${o}`} onClick={() => toggle(o)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}>
            {label(o)}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</span><div className="mt-1.5">{children}</div></label>;
}

export function MemberDialog({ open, onClose, member, onSave, saving }) {
  const { t } = useLang();
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    if (open) setF(member ? { ...EMPTY, ...member, goals: memberGoals(member), excluded_foods: member.excluded_foods || [], liked_foods: member.liked_foods || [], disliked_foods: member.disliked_foods || [] } : EMPTY);
  }, [open, member]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const isPrimary = member?.member_id === "primary";
  const valid = f.name && Number(f.age) > 0 && Number(f.height_cm) > 0 && Number(f.weight_kg) > 0;

  const submit = () => onSave({ ...f, goal: primaryGoal(f.goals), age: Number(f.age), height_cm: Number(f.height_cm), weight_kg: Number(f.weight_kg), water_goal: Math.min(20, Math.max(1, Number(f.water_goal) || 8)) });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{member ? t("edit_member") : t("add_member")}</DialogTitle>
          <DialogDescription>{t("household_sub")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("member_name")}><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="member-name" className="rounded-xl" /></Field>
            <Field label={t("age")}><Input type="number" value={f.age} onChange={(e) => set("age", e.target.value)} data-testid="member-age" className="rounded-xl" /></Field>
            <Field label={t("height")}><Input type="number" value={f.height_cm} onChange={(e) => set("height_cm", e.target.value)} data-testid="member-height" className="rounded-xl" /></Field>
            <Field label={t("weight")}><Input type="number" value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} data-testid="member-weight" className="rounded-xl" /></Field>
          </div>
          <Field label={t("sex")}><Chips multi={false} options={OPTS.sex} value={f.sex} onChange={(v) => set("sex", v)} label={t} testid="member-sex" /></Field>
          <Field label={t("activity")}><Chips multi={false} options={OPTS.activity_level} value={f.activity_level} onChange={(v) => set("activity_level", v)} label={t} testid="member-activity" /></Field>
          <Field label={t("goals_label")}><Chips multi options={OPTS.goals} value={f.goals || []} onChange={(v) => set("goals", v)} label={(g) => t(goalKey(g))} testid="member-goal" /></Field>
          <Field label={t("diet")}><Chips multi={false} options={OPTS.diet} value={f.diet} onChange={(v) => set("diet", v)} label={t} testid="member-diet" /></Field>
          <Field label={t("allergies")}><Chips options={ALLERGENS} value={f.allergies} onChange={(v) => set("allergies", v)} label={(a) => t(`al_${a}`)} testid="member-allergies" /></Field>
          <Field label={t("health_contexts")}><Chips options={HEALTH_CONTEXTS} value={f.health_contexts || []} onChange={(v) => set("health_contexts", v)} label={t} testid="member-health-contexts" /></Field>
          <p className="text-xs leading-relaxed text-muted-foreground">{t("health_context_note")}</p>
          <Field label={t("excluded")}><Input defaultValue={f.excluded_foods.join(", ")} onBlur={(e) => set("excluded_foods", list(e.target.value))} placeholder={t("excluded_ph")} data-testid="member-excluded" className="rounded-xl" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("liked")}><Input defaultValue={f.liked_foods.join(", ")} onBlur={(e) => set("liked_foods", list(e.target.value))} placeholder={t("liked_ph")} data-testid="member-liked" className="rounded-xl" /></Field>
            <Field label={t("disliked")}><Input defaultValue={f.disliked_foods.join(", ")} onBlur={(e) => set("disliked_foods", list(e.target.value))} placeholder={t("disliked_ph")} data-testid="member-disliked" className="rounded-xl" /></Field>
          </div>
          <Field label={t("water_goal")}><Input type="number" min={1} max={20} value={f.water_goal} onChange={(e) => set("water_goal", e.target.value)} data-testid="member-water-goal" className="rounded-xl" /></Field>
          <Field label={t("present_days")}><Chips options={DAYS} value={f.present_days} onChange={(v) => set("present_days", v)} label={(d) => t(d).slice(0, 3)} testid="member-days" /></Field>
          <div className="flex items-center justify-between rounded-2xl bg-secondary/60 px-4 py-3">
            <span className="font-medium text-sm">{t("participates")}</span>
            <Switch checked={f.participates} onCheckedChange={(v) => set("participates", v)} data-testid="member-participates" />
          </div>
          {isPrimary && <p className="text-xs text-muted-foreground">{t("primary_member")} — {t("edit_profile")} ↗</p>}
          <Button onClick={submit} disabled={!valid || saving} className="w-full rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="member-save">
            {saving ? t("saving") : t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
